/**
 * Design System Tokens - Dark Mode Only
 *
 * Centralized design values for dark mode UI.
 * Use these tokens throughout the application to ensure consistency.
 *
 * Usage:
 * import { colors, typography, spacing, effects } from '@/design/tokens';
 *
 * Example:
 * <div className={`${colors.surface.primary} ${typography.size.base}`}>
 *
 * Design Patterns:
 * - Text links on hover: Show border/underline, do NOT change text color
 * - Button hover: Can use opacity, brightness, or other effects
 */

// ============================================================================
// COLORS
// ============================================================================

export const colors = {
  // Primary accent colors
  primary: {
    blue: '#3B82F6',
    blueAlt: '#60A5FA', // Lighter blue for links/accents
    pink: '#E91E63',
    purple: '#9C27B0',
  },

  // Surface backgrounds
  surface: {
    primary: 'bg-dark-surface-primary',
    secondary: 'bg-dark-surface-secondary',
  },

  // Text colors
  text: {
    primary: 'text-dark-text',              // Main text, highest emphasis
    secondary: 'text-dark-text-secondary',  // Labels, less important text (lighter)
    tertiary: 'text-dark-text-tertiary',    // File paths, needs better contrast (darker than secondary)
    placeholder: 'text-dark-placeholder',
  },

  // Border colors
  border: {
    primary: 'border-dark-border',
    gray: {
      200: 'border-gray-200',
      800: 'border-gray-800',
    },
  },

  // Specific color values (hex codes)
  hex: {
    white: '#E6E6E6',  // Softer white matching dark-text for consistency
    black: '#000000',
    footer: '#0B0B0B',
    footerHeader: '#EAEAEA',
    footerLink: '#9CA3AF',
    transparent: 'transparent',
    // Dark mode specific colors
    surfacePrimary: '#17191C',    // dark-surface-primary equivalent
    surfaceSecondary: '#0A0E14',  // dark-surface-secondary equivalent
    textPrimary: '#E6E6E6',       // dark-text equivalent - softer white for reduced eye strain
    textSecondary: '#B0B0B0',     // dark-text-secondary equivalent
    textTertiary: '#7D7D7D',      // dark-text-tertiary equivalent - darker for file browser (rgb 125,125,125)
    textPlaceholder: '#A0A0A0',   // dark-placeholder equivalent - brighter for daylight visibility
    borderPrimary: '#2A2E34',     // dark-border equivalent
  },

  // Hover states
  hover: {
    background: 'hover:bg-black/10',
    backgroundAlt: 'hover:bg-dark-hover',
    border: 'hover:border-white',
    opacity: 'hover:opacity-90',
    brightness: 'hover:brightness-150',
    brightness200: 'hover:brightness-200',
  },

  // Overlay colors (for modals, dropdowns, backdrops)
  overlay: {
    light: 'bg-black/10',      // Subtle overlay
    default: 'bg-black/25',    // Premium modal overlay (Porsche-inspired)
    medium: 'bg-black/50',     // Stronger overlay
  },

  // Gray scale (for secondary elements, close buttons)
  gray: {
    300: 'text-gray-300',      // Close buttons, tertiary text
    400: 'text-gray-400',      // Secondary icons
  },

  // Gradients
  gradients: {
    hero: 'bg-gradient-hero-dark',
    primary: 'bg-gradient-to-r from-[#E91E63] to-[#9C27B0]',
  },
} as const;

// ============================================================================
// TYPOGRAPHY
// ============================================================================

export const typography = {
  // Font family
  // Primary font: Inter (defined in tailwind.config.js)
  // Fallbacks: system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif
  family: {
    sans: 'font-sans',  // Uses Inter from Tailwind config
  },

  // Font sizes
  size: {
    xs: 'text-xs',        // 12px - Footer headers
    sm: 'text-sm',        // 14px - Buttons, small text
    base: 'text-base',    // 16px - Footer links, body text
    lg: 'text-lg',        // 18px - Larger body text
    xl: 'text-xl',        // 20px
    '2xl': 'text-2xl',    // 24px
    '3xl': 'text-3xl',    // 30px
    '42': 'text-[42px]',  // 42px - Mobile headings
    '48': 'text-[48px]',  // 48px - Desktop headings

    // Hero/Section heading sizes (semantic tokens)
    // Use for major section headings like "How Darvin Works", "Darvin Blog", etc.
    hero: {
      mobile: 'text-[42px]',    // 42px - Hero heading on mobile
      desktop: 'text-[48px]',   // 48px - Hero heading on desktop
    },
  },

  // Font weights
  weight: {
    normal: 'font-normal',
    medium: 'font-medium',     // Standard for buttons
    semibold: 'font-semibold',
    bold: 'font-bold',
    light: 'font-[300]',       // Light weight (300)
  },

  // Letter spacing
  tracking: {
    wider: 'tracking-wider',
  },

  // Text transform
  transform: {
    uppercase: 'uppercase',
  },

  // Line heights
  leading: {
    normal: 'leading-normal',
    '1.4': 'leading-[1.4]',
    '1.15': 'leading-[1.15]',
  },
} as const;

// ============================================================================
// SPACING
// ============================================================================

export const spacing = {
  // Max widths (content constraints)
  maxWidth: {
    content: 'max-w-[1100px]',  // Primary content width
    input: 'max-w-[800px]',     // Input fields
    settings: 'max-w-[550px]',  // Settings side menu
  },

  // Padding
  padding: {
    xs: 'p-1',
    sm: 'p-2',
    md: 'p-4',
    lg: 'p-8',
    section: {
      mobile: 'px-8 pt-16 pb-10',
      desktop: 'px-8 pt-20 pb-12',
    },
  },

  // Gaps
  gap: {
    xs: 'gap-2',
    sm: 'gap-4',
    md: 'gap-5',
    lg: 'gap-8',
    xl: 'gap-16',
  },

  // Heights
  height: {
    blogCard: 'h-[220px]',
    blogImage: 'h-[130px]',
    blogContent: 'h-[80px]',
  },

  // Widths
  width: {
    dropdown: 'w-56',
    sideMenu: {
      collapsed: 'w-[56px]',
      pinned: 'w-[320px]',
      settings: 'w-[550px]',
    },
  },
} as const;

// ============================================================================
// EFFECTS
// ============================================================================

export const effects = {
  // Border radius
  borderRadius: {
    default: 'rounded',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    full: 'rounded-full',
  },

  // Shadows
  shadow: {
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
    custom: {
      card: '0 2px 8px rgba(0, 0, 0, 0.08)',
      dropdown: 'shadow-[0_2px_6px_rgba(0,0,0,0.18)]',
      glow: '0 0 20px rgba(233, 30, 99, 0.3), 0 0 40px rgba(156, 39, 176, 0.2)',
      modal: 'shadow-[0_8px_40px_0_rgba(0,0,0,0.9)]',
    },
  },

  // Transitions
  transition: {
    default: 'transition-all duration-300',
    opacity: 'transition-opacity duration-300',
    transform: 'transition-transform duration-300',
    padding: 'transition-[padding] duration-300',
  },

  // Hover effects
  hover: {
    lift: 'hover:-translate-y-1 hover:scale-[1.02]',
    shadow: 'hover:shadow-xl',
  },

  // Disabled states
  disabled: 'disabled:opacity-50',
} as const;

// ============================================================================
// LAYOUT
// ============================================================================

export const layout = {
  // Container patterns
  container: {
    centered: 'mx-auto',
    fullWidth: 'w-full',
  },

  // Grid configurations
  grid: {
    footer: {
      mobile: 'grid-cols-2 gap-x-8 gap-y-10',
      desktop: 'grid-cols-3 gap-16',
    },
    blog: {
      mobile: 'grid-cols-1',
      desktop: 'grid-cols-1 md:grid-cols-3 gap-5',
    },
  },

  // Flex patterns
  flex: {
    center: 'flex items-center justify-center',
    between: 'flex items-center justify-between',
    column: 'flex flex-col',
    columnStart: 'flex flex-col items-start',
  },

  // Positioning
  position: {
    absolute: 'absolute',
    relative: 'relative',
    fixed: 'fixed',
  },

  // Z-index
  zIndex: {
    base: 'z-10',
    dropdown: 'z-20',
    modal: 'z-50',
  },
} as const;

// ============================================================================
// ANIMATIONS
// ============================================================================

export const animations = {
  // Opacity transitions
  fadeIn: 'opacity-0 group-hover:opacity-100 transition-opacity duration-300',
  fadeOut: 'opacity-100 group-hover:opacity-0 transition-opacity duration-300',

  // Transform transitions
  scaleUp: 'group-hover:scale-105 transition-transform duration-300',

  // Max height for accordions
  accordion: {
    open: 'max-h-[500px] opacity-100',
    closed: 'max-h-0 opacity-0',
  },
} as const;

// ============================================================================
// COMPOSITE COMPONENTS
// ============================================================================

export const components = {
  // Button styles
  button: {
    // Standard primary button - Use for most buttons throughout the app
    primary: `px-4 py-2 bg-primary-blue text-white rounded-full
      hover:opacity-90 transition-opacity duration-300
      disabled:opacity-50 disabled:cursor-not-allowed
      flex items-center justify-center gap-2
      font-semibold text-sm tracking-wide`,

    // Large primary button - Use for prominent CTAs in modals and landing pages
    primaryLarge: `px-6 py-3 h-[48px] bg-primary-blue text-white rounded-full
      hover:opacity-90 transition-opacity duration-300
      disabled:opacity-50 disabled:cursor-not-allowed
      flex items-center justify-center gap-3
      font-semibold text-base tracking-wide`,

    // Compact primary button - Use for toolbars and compact UIs
    primaryCompact: `px-3 py-1.5 bg-primary-blue text-white rounded-full
      hover:opacity-90 transition-opacity duration-300
      disabled:opacity-50 disabled:cursor-not-allowed
      flex items-center justify-center gap-2
      font-medium text-sm tracking-wide`,

    secondary: `px-4 py-1 bg-transparent text-dark-text
      border border-dark-border rounded-full
      hover:opacity-90 transition-opacity disabled:opacity-50
      font-medium text-sm`,

    footer: `transition-all duration-300
      border border-transparent hover:border-white
      px-2 py-1 rounded`,
  },

  // Card styles
  card: {
    blog: `relative bg-white dark:bg-[#0F1419]
      flex flex-col h-[220px]
      rounded-xl overflow-hidden
      border border-dark-border
      hover:cursor-pointer hover:shadow-xl hover:-translate-y-1 hover:scale-[1.02]
      transition-all duration-300 group`,
  },

  // Form & Input styles
  //
  // VISUAL HIERARCHY PRINCIPLE:
  // Input text should be LARGER than label text to create clear primary/secondary hierarchy.
  // This matches industry standards (ChatGPT, GitHub, Notion, Stripe) where:
  // - Labels are 14px (secondary information, guides the user)
  // - Input text is 16px (primary focus, where user enters/reads data)
  //
  // Help icons should match label size (14px) for visual consistency.
  // Tooltips should be left-aligned next to labels (proximity principle) using gap-2,
  // NOT right-aligned with justify-between.
  //
  form: {
    // Text input - Use for standard text fields in forms
    // NOTE: Uses text-base (16px) for better readability and hierarchy
    input: `w-full pl-4 py-2.5 rounded-lg
      text-base text-dark-text
      placeholder-dark-placeholder
      bg-dark-surface-secondary
      focus:outline-none`,

    // Text input with action button (e.g., undo)
    // Use pr-10 when action button is present, pr-4 otherwise
    // NOTE: Uses text-base (16px) for input text
    inputWithAction: (hasAction: boolean) => `w-full pl-4 py-2.5 rounded-lg
      ${hasAction ? 'pr-10' : 'pr-4'}
      text-base text-dark-text
      placeholder-dark-placeholder
      bg-dark-surface-secondary
      focus:outline-none`,

    // Form label - Use text-sm (14px) for labels, smaller than input text
    label: `text-sm text-dark-text flex items-center gap-2`,

    // Label with help icon on the right (DEPRECATED - use label with gap-2 instead)
    // Prefer left-aligned tooltips for better visual association
    labelWithHelp: `text-sm text-dark-text flex items-center justify-between`,

    // Help icon size - Should match label text size (14px)
    helpIconSize: 14,

    // Error message below input
    error: `text-red-500 text-sm`,

    // Form action buttons container
    actions: `flex items-center justify-end gap-8`,

    // Cancel/secondary action button
    cancelButton: `text-sm text-dark-text hover:brightness-200 transition-all duration-300`,

    // Primary submit button (uses components.button.primary internally)
    // Already defined in components.button.primary
  },

  // Dropdown styles
  dropdown: {
    container: `w-56 p-1 rounded-md
      bg-black
      text-white
      border border-gray-800
      shadow-[0_2px_6px_rgba(0,0,0,0.18)]`,

    item: `flex items-center px-4 py-2.5 w-full gap-3
      hover:bg-white/10
      rounded-lg font-medium text-sm`,
  },

  // Modal styles
  //
  // Premium Porsche-inspired modal design
  // Reference: SubscriptionsModal for the canonical implementation
  //
  modal: {
    // Modal overlay backdrop - Premium subtle 25% opacity
    overlay: `bg-black/25 backdrop-blur-sm`,

    // Modal close button (X icon) - Premium gray with brightness hover
    closeButton: `text-gray-300 hover:brightness-125 transition-all duration-300`,

    // Modal action buttons container - Same spacing as form actions (32px gap)
    actions: `flex items-center justify-end gap-8`,
  },
} as const;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Combines class names conditionally
 */
export function cn(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

/**
 * Returns responsive class based on isCompactView
 */
export function responsive(compactClass: string, defaultClass: string, isCompact: boolean) {
  return isCompact ? compactClass : defaultClass;
}

// ============================================================================
// EXPORT ALL
// ============================================================================

export const designTokens = {
  colors,
  typography,
  spacing,
  effects,
  layout,
  animations,
  components,
} as const;

export default designTokens;
